import { useEffect, useState } from 'react';
import { GwpModal } from './components/GwpModal/index';
import { useCart } from './context/CartContext';
import { emit } from './utilities/eventBus';
import type { GwpProduct, SelectedGift, GiftStateType } from './utilities/types.ts';

// Window API types
type GwpSnapshot = {
  giftState: GiftStateType;
  requiredSpend: number;
  currentSpend: number;
  selectedGift: SelectedGift | null;
  giftProducts: GwpProduct[];
};

type RefetchOptions = {
  awaitUpdate?: boolean;
  timeoutMs?: number;
};

type RefetchResult = {
  ok: boolean;
  updated?: boolean;
};

type FreeGiftAPI = {
  openModal: () => void;
  closeModal: () => void;
  isModalOpen: boolean;
  state: () => any;
  getState: () => GwpSnapshot | any;
  chooseGift: (variantId: string, properties?: Record<string, any>) => Promise<void>;
  removeGift: () => Promise<void>;
  refetch: (options?: RefetchOptions) => Promise<RefetchResult>;
  snapshot?: GwpSnapshot;
};

type WindowWithFreeGift = Window & {
  freeGift?: FreeGiftAPI;
};

// Custom event types
interface GwpEventDetail {
  ready?: boolean;
  reason?: string;
  requiredSpend?: number;
  currentSpend?: number;
  giftState?: GiftStateType;
  selectedGift?: SelectedGift | null;
  giftProducts?: GwpProduct[];
}

declare global {
  interface WindowEventMap {
    'gwp:ready': CustomEvent<GwpEventDetail>;
    'gwp:event': CustomEvent<GwpEventDetail>;
    'gwp:targetReached': CustomEvent<GwpEventDetail>;
    'gwp:openDrawer': CustomEvent;
    'cart:changed': CustomEvent<GwpEventDetail>;
  }
}

function App() {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { gwp, refetchCart } = useCart();

  // Expose window.freeGift API and emit gwp:ready once available.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const windowWithApi = window as WindowWithFreeGift;

    const api: FreeGiftAPI = {
      openModal: () => setModalOpen(true),
      closeModal: () => setModalOpen(false),
      isModalOpen: modalOpen,
      // Prefer snapshot getter, but keep state() for backwards-compat.
      state: () => gwp,
      getState: () => windowWithApi.freeGift?.snapshot || gwp,
      chooseGift: async (variantId: string, properties?: Record<string, any>) => {
        const { ok } = await gwp.chooseGift(variantId, properties);
        if (ok) {
          emit('cart:changed', { reason: 'gift-chosen' });
        }
      },
      removeGift: async () => {
        const { ok } = await gwp.removeGift();
        if (ok) {
          emit('cart:changed', { reason: 'gift-removed' });
        }
      },
      /**
       * Requests a refetch/recompute of the cart + GWP state.
       */
      refetch: async (options: RefetchOptions = {}): Promise<RefetchResult> => {
        const awaitUpdate = typeof options.awaitUpdate === 'boolean' ? options.awaitUpdate : true;
        const timeoutMs = typeof options.timeoutMs === 'number' ? options.timeoutMs : 8000;
        
        try {
          if (!awaitUpdate) {
            await refetchCart();
            return { ok: true };
          }
          
          // Await the next gwp:event or timeout.
          const updated = await new Promise<boolean>((resolve) => {
            let settled = false;
            const onUpdate = () => {
              if (settled) return;
              settled = true;
              window.removeEventListener('gwp:event', onUpdate);
              resolve(true);
            };
            window.addEventListener('gwp:event', onUpdate, { once: true });
            setTimeout(() => {
              if (settled) return;
              settled = true;
              window.removeEventListener('gwp:event', onUpdate);
              resolve(false);
            }, timeoutMs);
          });
          
          await refetchCart();
          return { ok: true, updated: !!updated };
        } catch {
          return { ok: false };
        }
      }
    };

    if (!windowWithApi.freeGift) {
      windowWithApi.freeGift = {} as FreeGiftAPI;
    }
    Object.assign(windowWithApi.freeGift, api);

    // Notify theme that API is ready.
    emit('gwp:ready', { ready: true });
  }, [modalOpen, gwp, refetchCart]);

  // Broadcast gwp state changes and synchronize a stable snapshot on window.
  useEffect(() => {
    if (!gwp) return;
    
    /** Snapshot is a pure data object for external consumers. */
    const snapshot: GwpSnapshot = {
      giftState: gwp.giftState,
      requiredSpend: gwp.requiredSpend,
      currentSpend: gwp.currentSpend,
      selectedGift: gwp.selectedGift,
      giftProducts: gwp.giftProducts
    };
    
    try {
      const windowWithApi = window as WindowWithFreeGift;
      windowWithApi.freeGift = windowWithApi.freeGift || ({} as FreeGiftAPI);
      windowWithApi.freeGift.snapshot = snapshot;
    } catch {
      // Ignore errors.
    }

    emit('gwp:event', snapshot);

    if (gwp.giftState === 'ELIGIBLE') {
      emit('gwp:targetReached', {
        requiredSpend: gwp.requiredSpend,
        currentSpend: gwp.currentSpend
      });
    }
  }, [gwp]);

  // Listen for inbound events from the theme.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleCartChanged = () => {
      // Theme signals cart changed → refetch.
      // Note: CartContext emits refetch on choose/remove; this covers external changes.
      // We rely on CartContext effect hooks; nothing needed here.
    };

    const handleOpenDrawer = () => {
      setModalOpen(true);
    };

    window.addEventListener('cart:changed', handleCartChanged);
    window.addEventListener('gwp:openDrawer', handleOpenDrawer);

    return () => {
      window.removeEventListener('cart:changed', handleCartChanged);
      window.removeEventListener('gwp:openDrawer', handleOpenDrawer);
    };
  }, []);

  // Optional: open on mount for testing.
  // useEffect(() => setModalOpen(true), []);

  return <GwpModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />;
}

export default App;